import { Button } from '@/components/ui/button';
import { RequiredMark } from '@/components/ui/required-mark';
import { Tooltip, TooltipShield } from '@/components/ui/tooltip';
import { TriangleAlert } from 'lucide-react';
import type { ReactNode } from 'react';
import { useId } from 'react';

/**
 * The pieces the TWO sections of Réglages > Personas share (§11.5, planche
 * `ReglagesPersonas`).
 *
 * The scale and the tone library are the same object with different contents: a
 * list of rows, each a short field and a long one with its counter, a trash
 * whose confirmation sits INLINE under the row, a dashed « Ajouter », and one
 * button that saves the block. Written twice, they had already diverged on the
 * width of the name field within one lot - and the half that drifts is always
 * the one nobody reads (the argument `ui/callout.tsx` makes about seven
 * spellings of one warning).
 *
 * They live here rather than in `components/ui/` because they are not generic:
 * the confirmation's ground, the counter's placement and the footer's
 * « Modifications non enregistrées. » are the shape of THIS screen. The day a
 * third reference list arrives (§11.3 seeds two more), this file is what it
 * mounts.
 */

const FIELD_BOX =
  'w-full rounded-md border-2 border-gray-300 bg-white focus-visible:border-bleu-nuit focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1';

export interface ReferenceFieldProps {
  readonly label: string;
  readonly testId: string;
  readonly value: string;
  readonly max: number;
  readonly placeholder: string;
  /**
   * Whether the field takes the full width of the column.
   *
   * The ONE écart the maquette assumes, and it is worth the prop rather than a
   * second component: « un champ pleine largeur pour "chaleureux" ment sur ce
   * qu'on attend » (README du dossier de maquettes). It holds for a tone's name
   * and for the first name of the editor's modal, and for nothing else.
   */
  readonly narrow?: boolean;
  readonly onChange: (next: string) => void;
}

/** A one-line field with its label. No counter: a name is read at a glance. */
export function ReferenceField({
  label,
  testId,
  value,
  max,
  placeholder,
  narrow = false,
  onChange,
}: ReferenceFieldProps) {
  const id = useId();
  return (
    <div className="space-y-1">
      <label htmlFor={id} className="block font-semibold text-gray-800 text-xs">
        {label}
        <RequiredMark />
      </label>
      <input
        id={id}
        data-testid={testId}
        value={value}
        required
        aria-required="true"
        maxLength={max}
        placeholder={placeholder}
        onChange={(event) => onChange(event.currentTarget.value)}
        className={`${FIELD_BOX} h-10 px-3 text-sm ${narrow ? 'max-w-60' : ''}`}
      />
    </div>
  );
}

/** The long field, with the counter of its input bound (H13). */
export function ReferenceArea({
  label,
  testId,
  value,
  max,
  placeholder,
  onChange,
}: Omit<ReferenceFieldProps, 'narrow'>) {
  const id = useId();
  return (
    <div className="space-y-1">
      <label htmlFor={id} className="block font-semibold text-gray-800 text-xs">
        {label}
        <RequiredMark />
      </label>
      <textarea
        id={id}
        data-testid={testId}
        value={value}
        required
        aria-required="true"
        rows={3}
        maxLength={max}
        placeholder={placeholder}
        onChange={(event) => onChange(event.currentTarget.value)}
        className={`${FIELD_BOX} resize-y p-3 text-sm leading-relaxed`}
      />
      {/* A bound of INPUT and not of meaning (H13): grey, under the field, and
          it warns about nothing. */}
      <p className="text-right text-gray-500 text-xs leading-4">
        {value.length} / {max} caractères
      </p>
    </div>
  );
}

/**
 * A 36 px square control that carries only an icon.
 *
 * `aria-label` is its ONLY name - the icon is `aria-hidden` - which makes these
 * strings a contract with the tests that click them by name, exactly like the
 * rail's labels.
 */
export function IconButton({
  label,
  tone = 'default',
  testId,
  disabled = false,
  blocked = null,
  onClick,
  children,
}: {
  label: string;
  tone?: 'default' | 'danger';
  testId?: string;
  disabled?: boolean;
  /**
   * Why this control is closed, as a SENTENCE, or `null` when it is open.
   *
   * The difference with `disabled`, and the reason both exist: a `disabled`
   * button receives neither pointer nor focus, so its bubble never opens. That
   * costs nothing for the arrows, whose bubble only repeats a name that stays
   * readable; it costs everything for a refusal, whose sentence exists ONLY
   * while the control is closed and is therefore unreachable exactly when it
   * is needed. So a blocked control is wrapped in `TooltipShield` - focusable
   * while closed, holding a truly `disabled` button - and its bubble says the
   * reason instead of the name. Added for « Dupliquer » (§4.2, issue #117),
   * whose cap has nowhere else to refuse.
   *
   * A control closed by `disabled` alone keeps the shape it always had: no
   * wrapper, no extra tab stop.
   */
  blocked?: string | null;
  onClick: () => void;
  children: ReactNode;
}) {
  const colours =
    tone === 'danger'
      ? 'border-rouge text-rouge hover:bg-rouge hover:text-white'
      : 'border-gray-300 text-bleu-nuit hover:bg-gray-100';
  const closed = disabled || blocked !== null;
  const control = (
    <button
      type="button"
      aria-label={label}
      {...(testId === undefined ? {} : { 'data-testid': testId })}
      disabled={closed}
      onClick={onClick}
      className={`flex h-9 w-9 cursor-pointer items-center justify-center rounded-md border-2 disabled:cursor-not-allowed disabled:border-gray-200 disabled:bg-gray-100 disabled:text-gray-500 ${colours}`}
    >
      {children}
    </button>
  );
  return (
    // The bubble says exactly what `aria-label` says, so `Tooltip` deliberately
    // leaves `aria-describedby` off: a screen reader would read the word twice.
    // A REFUSAL says something else, and then the description is wanted.
    <Tooltip label={blocked ?? label}>
      {blocked === null ? (
        control
      ) : (
        <TooltipShield closed={closed} className="inline-flex">
          {control}
        </TooltipShield>
      )}
    </Tooltip>
  );
}

/**
 * The INLINE confirmation of a deletion (planche `ReglagesPersonas`).
 *
 * Inline and not a modal, because that is what the planche draws and because
 * the sentence it carries is about the row it sits under - a modal would take
 * the row off the screen at the moment the operator is deciding about it. It
 * still declares `role="alertdialog"`, so it is announced.
 */
export function DeleteConfirm({
  testId,
  label,
  busy,
  indented = false,
  confirmLabel = 'Supprimer',
  onCancel,
  onConfirm,
  children,
}: {
  testId: string;
  label: string;
  busy: boolean;
  /** Aligned under the fields rather than under the row number, in the scale. */
  indented?: boolean;
  /**
   * What the red button says. « Supprimer » for the two reference lists this
   * block was written for; issue #59 mounts it on a REVOCATION, and a button
   * that said « Supprimer » there would promise the row was about to disappear -
   * a revoked link stays in the list, and that is the point of §11.4.
   */
  confirmLabel?: string;
  onCancel: () => void;
  onConfirm: () => void;
  children: ReactNode;
}) {
  return (
    <div
      role="alertdialog"
      aria-label={label}
      data-testid={testId}
      className={`space-y-2 rounded-md border-2 border-rouge bg-rouge-tint p-3 ${indented ? 'ml-10' : ''}`}
    >
      <div className="flex items-start gap-2">
        <TriangleAlert
          size={16}
          strokeWidth={2}
          aria-hidden="true"
          className="mt-0.5 shrink-0 text-rouge"
        />
        <p className="text-gray-800 text-sm leading-5">{children}</p>
      </div>
      <div className="flex items-center gap-2">
        <Button type="button" variant="ghost" size="sm" onClick={onCancel}>
          Annuler
        </Button>
        <Button type="button" variant="destructive" size="sm" disabled={busy} onClick={onConfirm}>
          {confirmLabel}
        </Button>
      </div>
    </div>
  );
}

/**
 * The block save of a section: its button, why it is greyed, and the refusal.
 *
 * Shared by the two sections because the planche gives them the same footer,
 * and because the one thing they must not disagree on is what a disabled button
 * means: nothing to save, or something that cannot be.
 */
export function SectionFooter({
  testId,
  label,
  blocked,
  dirty,
  saving,
  error,
  onSave,
}: {
  testId: string;
  label: string;
  blocked: string | null;
  dirty: boolean;
  saving: boolean;
  error: string | null;
  onSave: () => void;
}) {
  return (
    <div className="space-y-2">
      {/* Mounted from the first render, empty or not: a live region created at
          the same moment it fills is not read out. */}
      <p aria-live="polite" className="text-rouge-deep text-sm">
        {error ?? blocked}
      </p>
      <div className="flex items-center gap-3">
        <Button
          type="button"
          data-testid={testId}
          disabled={saving || !dirty || blocked !== null}
          onClick={onSave}
        >
          {label}
        </Button>
        {dirty && blocked === null ? (
          <span className="text-gray-600 text-sm">Modifications non enregistrées.</span>
        ) : null}
      </div>
    </div>
  );
}
